// components/EmailTemplateModal.jsx
import React, { useState, useEffect } from 'react';
import {
    Modal, Form, Input, Button, message
} from 'antd';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API from '../../../API/API';
import { formatUSPhone } from '../../../Utils';
import { roundMoney, getBalance, computePaidAmount, formatMoney } from '../../../utils/money';

const EmailTemplateModal = ({ visible, onCancel, onSend, lead, userData, senderAgentId }) => {
    const [form] = Form.useForm();
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const company = {
        name: "Premium Used Auto Parts",
        logo: "https://premiumusedautoparts.com/puap-og.jpg",
        address: "10940 White Rock Rd, Suite #210, Rancho Cordova, CA 95670",
        phone: "1-800-606-1107",
        email: "sales@premiumusedautoparts.com",
        website: "premiumusedautoparts.com",
    };

    const vehicleString = lead?.vehicleInfo
        ? `${lead.vehicleInfo.part || ''} for ${lead.vehicleInfo.make || ''} ${lead.vehicleInfo.model || ''} ${lead.vehicleInfo.year || ''}`.trim()
        : 'your requested part';

    // Current price only: quotedValue is the full re-quote history (oldest
    // first), and older prices shouldn't reach the customer after a re-quote.
    // Its newest entry matches totalAmount.
    const latestQuotePrice = lead?.quotedValue?.[lead.quotedValue.length - 1]?.price || lead?.totalAmount;
    const quoteText = latestQuotePrice ? formatMoney(latestQuotePrice) : 'your quote';

    // What the customer has actually paid, counted like the Payment Details tab.
    // Leads without payment records fall back to the stored paid amount.
    const paidAmount = lead?.advancePayments?.length
        ? computePaidAmount(lead.advancePayments)
        : roundMoney(lead?.paidAmount);

    const orderNumber = lead?.orderNumber || 'your order';

    // Who the email goes out from. The backend sends from the Gmail of the agent
    // with this id (senderAgentId, else the logged-in user), and only for SALES-AGENT users.
    const getUserId = (user) => (user && typeof user === 'object' ? (user._id || user.id) : user) || null;
    const loginUserId = getUserId(userData);
    const senderId = senderAgentId || loginUserId;
    const isLoginUserSender = Boolean(senderId) && String(senderId) === String(loginUserId);
    const senderUser = isLoginUserSender
        ? userData
        : [lead?.handledBy, lead?.agentId, lead?.assignedTo].find(
            (candidate) => candidate && typeof candidate === 'object' && String(getUserId(candidate)) === String(senderId)
        ) || null;
    const senderName = [senderUser?.firstName, senderUser?.lastName].filter(Boolean).join(' ').trim();
    let fromText = 'Not available';
    if (senderUser?.email) {
        fromText = senderName ? `${senderName} <${senderUser.email}>` : senderUser.email;
    } else if (senderId && !isLoginUserSender) {
        fromText = senderName ? `${senderName} (assigned agent's Gmail)` : "Assigned agent's Gmail";
    }
    // A sender picked by the caller (My Leads) is already a sales agent; otherwise check the role
    const canSendAsSender = isLoginUserSender ? userData?.role === 'SALES-AGENT' : Boolean(senderId);

    const companySignature = `
        <p><br/></p>
        <div style="margin-top: 20px; padding-top: 12px; border-top: 2px solid #e0e0e0; font-family: Arial, sans-serif; color: #555; font-size: 14px;">
            <strong style="color: #333; font-size: 16px;">${company.name}</strong><br/>
            ${company.address}<br/>
            Phone: <a href="tel:${company.phone.replace(/[^0-9]/g, '')}" style="color: #1890ff; text-decoration: none;">${company.phone}</a> |
            Email: <a href="mailto:${company.email}" style="color: #1890ff; text-decoration: none;">${company.email}</a><br/>
            Website: <a href="https://${company.website}" target="_blank" style="color: #1890ff; text-decoration: none;">${company.website}</a>
        </div>
    `;

    useEffect(() => {
        if (!visible || !lead) return;

        const customerName = lead.personalInfo?.fullName || 'Valued Customer';
        const agentFirstName = String(userData?.firstName || '').trim();
        const agentName = agentFirstName
            ? agentFirstName.charAt(0).toUpperCase() + agentFirstName.slice(1)
            : 'The Team';
        // formatUSPhone gives "—" for an empty number, so the line is left out
        // unless the number is valid.
        const agentPhone = formatUSPhone(userData?.directNumber);
        const agentPhoneLine = agentPhone.isValid
            ? `<br/>Phone: <a href="tel:${agentPhone.e164}">${agentPhone.formatted}</a>`
            : '';

        let dynamicSubject = '';
        let dynamicBodyContent = '';

        switch (lead.leadStage) {
            case 'Quoted':
                dynamicSubject = `Your Quote for ${vehicleString}`;
                dynamicBodyContent = `
                    <p><strong>Hi ${customerName},</strong></p>
                    <p>Thank you for your inquiry! Here is the quote for the part you requested:</p>
                    <h2><strong>Quote Details</strong></h2>
                    <p><strong>Vehicle:</strong> ${vehicleString}</p>
                    <p><strong>Quote Price:</strong> ${quoteText}</p>
                    <p>Please let us know if you'd like to proceed or if you have any questions.</p>
                    <p>Best regards,<br/>${agentName}${agentPhoneLine}</p>
                `;
                break;

            case 'Pending Payment':
                const totalAmount = roundMoney(lead.totalAmount);
                const remaining = getBalance(totalAmount, paidAmount);

                dynamicSubject = `Payment Request - ${vehicleString}`;
                dynamicBodyContent = `
                    <p><strong>Hi ${customerName},</strong></p>
                    <p>We have prepared the payment details for your requested part.</p>
                    <h2><strong>Order Summary</strong></h2>
                    <p><strong>Part:</strong> ${vehicleString}</p>
                    <p><strong>Total Amount:</strong> ${formatMoney(totalAmount)}</p>
                    <p><strong>Advance Paid:</strong> ${formatMoney(paidAmount)}</p>
                    <p><strong>Remaining Balance:</strong> ${formatMoney(remaining)}</p>
                    <p>Please complete the payment at your earliest convenience so we can proceed with shipping.</p>
                    <p>Thank you!<br/>${agentName}${agentPhoneLine}</p>
                `;
                break;

            case 'Payment Completed':
                dynamicSubject = `Payment Received - Thank You!`;
                dynamicBodyContent = `
                    <p><strong>Hi ${customerName},</strong></p>
                    <p>Thank you for your payment! We have received the full amount for your order.</p>
                    <h2><strong>Order Confirmed</strong></h2>
                    <p><strong>Part:</strong> ${vehicleString}</p>
                    <p><strong>Total Paid:</strong> ${formatMoney(paidAmount)}</p>
                    <p>Your order is now being processed and will be shipped soon.</p>
                    <p>Best regards,<br/>${agentName}${agentPhoneLine}</p>
                `;
                break;

            case 'Sales Made':
                dynamicSubject = `Order Confirmation - ${orderNumber}`;
                dynamicBodyContent = `
                    <p><strong>Hi ${customerName},</strong></p>
                    <p>Your order has been confirmed! Thank you for choosing us.</p>
                    <h2><strong>Order Details</strong></h2>
                    <p><strong>Order Number:</strong> ${orderNumber}</p>
                    <p><strong>Part:</strong> ${vehicleString}</p>
                    <p><strong>Business Name:</strong> ${lead.bussinessName || 'N/A'}</p>
                    <p>We will keep you updated on the shipping status.</p>
                    <p>Thank you!<br/>${agentName}${agentPhoneLine}</p>
                `;
                break;

            default: {
                // US order (Year Make Model Part), key info first so it isn't cut off on mobile
                const vehicleDetails = [lead.vehicleInfo?.year, lead.vehicleInfo?.make, lead.vehicleInfo?.model, lead.vehicleInfo?.part]
                    .map((value) => String(value || '').trim())
                    .filter(Boolean)
                    .join(' ');
                const subjectVehicle = vehicleDetails || 'Requested Part';
                const bodyVehicle = vehicleDetails ? `the <strong>${vehicleDetails}</strong> you requested` : 'the part you requested';
                dynamicSubject = `Your ${subjectVehicle} – Premium Used Auto Parts`;
                dynamicBodyContent = `
                    <p><strong>Hi ${customerName},</strong></p>
                    <p>Thank you for contacting Premium Used Auto Parts regarding ${bodyVehicle}.</p>
                    <p>We're checking availability and pricing for you. To make sure we match the exact part, please reply with your <strong>VIN</strong> (17-digit vehicle number), or call us at <strong>${company.phone}</strong>.</p>
                    <p>Best regards,<br/>${agentName}${agentPhoneLine}</p>
                `;
                break;
            }
        }

        const fullBody = dynamicBodyContent + companySignature;
        setSubject(dynamicSubject);
        setBody(fullBody);
    }, [visible, lead, userData]);

    const handleSendEmail = async () => {
        if (!subject.trim() || !body.trim()) {
            message.error('Subject and body are required');
            return;
        }

        setIsLoading(true);
        try {
            const textContent = body
                .replace(/<p>/g, '\n')
                .replace(/<br\s*\/?>/g, '\n')
                .replace(/&nbsp;/g, ' ')
                .replace(/<\/?[^>]+(>|$)/g, '')
                .replace(/Best regards,[^]+/, '');

            const emailData = {
                to: lead.personalInfo.email,
                subject: subject,
                text: textContent.trim(),
                html: `<html><body style="font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; color: #333;">${body}</body></html>`,
                agentId: senderAgentId || userData?.id,
            };

            const response = await API.SendEmail(emailData);

            if (response?.status) {
                message.success('Email sent successfully!');
                setSubject('');
                setBody('');
                if (onSend) {
                    onSend(response);
                } else {
                    onCancel?.();
                }
            } else {
                message.error(response?.error || response?.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Failed to send email:', error);
            message.error(error?.response?.data?.error || error?.response?.data?.message || 'Failed to send email. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ]
    };

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link'
    ];

    return (
        <Modal
            title="Send Email"
            open={visible}
            onCancel={onCancel}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button key="send" type="primary" loading={isLoading} onClick={handleSendEmail}>
                    Send Email
                </Button>,
            ]}
            width={800}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="From"
                    extra={!canSendAsSender ? (
                        <span style={{ color: '#d46b08' }}>Only sales agents can send this email.</span>
                    ) : null}
                >
                    <Input value={fromText} disabled />
                </Form.Item>
                <Form.Item label="To">
                    <Input value={lead?.personalInfo?.email} disabled />
                </Form.Item>
                <Form.Item label="Subject" required>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                </Form.Item>
                <Form.Item label="Body" required>
                    <ReactQuill
                        theme="snow"
                        value={body}
                        onChange={setBody}
                        modules={modules}
                        formats={formats}
                        style={{ height: '320px', marginBottom: '60px' }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EmailTemplateModal;
